import {
	Accordion,
	Checkbox,
	Divider,
	Form,
	Input,
} from '@mairistem/composants';
import { useService } from '@mairistem/comptabilite';
import {
	Combobox,
	type Etiquette,
	getErrorMessage,
	Icon,
	useEtiquette,
} from '@mairistem/finances-utils';
import type { DropdownOption } from '@mairistem/liste';
import React from 'react';
import { toast } from 'react-toastify';
import type FiltresArbitrageMasse from '../../interfaces/simulation/filtresArbitrageMasse';
import type Simulation from '../../interfaces/simulation/simulation';
import { getUtilisateursDetailMontants } from '../../services/simulation/simulation';
import { COULEURS_ETIQUETTES } from '../../utils/simulation/etiquette';
import { formaterNomUtilisateur } from '../../utils/simulation/utilisateur';
import './ArbitrageMasseFiltres.css';
import ArbitrageMasseFiltreReferentiel from './ArbitrageMasseFiltreReferentiel';

// Memes libelles que les regroupements Section / Sens des feuilles de saisie
const SECTION_OPTIONS: DropdownOption[] = [
	{ 'data-testid': 'sectionF', key: 'F', text: 'Fonctionnement', value: 'F' },
	{ 'data-testid': 'sectionI', key: 'I', text: 'Investissement', value: 'I' },
];

const SENS_OPTIONS: DropdownOption[] = [
	{ 'data-testid': 'sensD', key: 'D', text: 'Dépense', value: 'D' },
	{ 'data-testid': 'sensR', key: 'R', text: 'Recette', value: 'R' },
];

// "1 234,56" -> 1234.56 ; null si vide ou non numerique
const parseMontant = (saisie: string): number | null => {
	const normalise = saisie.replace(/\s/g, '').replace(',', '.');
	if (normalise === '') return null;

	const montant = Number(normalise);

	return Number.isFinite(montant) ? montant : null;
};

interface MontantFiltreInputProps {
	'data-testid': string;
	onChange: (montant: number | null) => void;
	value: number | null;
}

// Currency ne sait pas rester vide (il affiche et renvoie 0) : or une borne
// vide doit signifier "pas de filtre". Le montant n'est transmis qu'a la
// sortie du champ (ou sur Entree), pour ne pas relire la liste a chaque frappe.
const MontantFiltreInput = ({
	'data-testid': dataTestId,
	onChange,
	value,
}: MontantFiltreInputProps) => {
	const [saisie, setSaisie] = React.useState(
		value === null ? '' : String(value).replace('.', ','),
	);

	// Resynchronise la saisie quand la valeur change de l'exterieur (ex:
	// reinitialisation des filtres)
	// biome-ignore lint/correctness/useExhaustiveDependencies: seul un changement de valeur doit resynchroniser la saisie
	React.useEffect(() => {
		if (parseMontant(saisie) !== value) {
			setSaisie(value === null ? '' : String(value).replace('.', ','));
		}
	}, [value]);

	const valider = () => {
		const montant = parseMontant(saisie);
		// Saisie invalide : on vide le champ plutot que d'appliquer un filtre
		// que l'utilisateur ne voit pas
		if (montant === null) setSaisie('');
		if (montant !== value) onChange(montant);
	};

	return (
		<Input
			fluid
			input={{
				'data-testid': dataTestId,
				inputMode: 'decimal',
				style: { textAlign: 'right' },
			}}
			onBlur={valider}
			onChange={(_e, { value: nouvelleSaisie }: { value: string }) =>
				setSaisie(nouvelleSaisie)
			}
			onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
				if (e.key === 'Enter') valider();
			}}
			placeholder="Aucun"
			value={saisie}
		/>
	);
};

interface FiltreMultipleProps<T extends string | number> {
	'data-testid': string;
	label: string;
	loading?: boolean;
	onChange: (valeurs: T[]) => void;
	options: DropdownOption[];
	placeholder: string;
	value: T[];
}

// Liste a choix multiples avec recherche, commune a tous les filtres
const FiltreMultiple = <T extends string | number>({
	'data-testid': dataTestId,
	label,
	loading = false,
	onChange,
	options,
	placeholder,
	value,
}: FiltreMultipleProps<T>) => (
	<Form.Field
		control={Combobox}
		data-testid={dataTestId}
		label={label}
		loading={loading}
		menuPortalTarget={document.body}
		multiple
		onChange={(_e, { value: valeurs }: { value: T[] }) => onChange(valeurs)}
		options={options}
		placeholder={placeholder}
		search
		selection
		value={value}
	/>
);

interface ArbitrageMasseFiltresProps {
	filtres: FiltresArbitrageMasse;
	// Job de detail courant, dont les utilisateurs alimentent le filtre
	identifiantJob: number | null;
	onChange: (filtres: Partial<FiltresArbitrageMasse>) => void;
	simulation: Simulation;
}

const ArbitrageMasseFiltres = ({
	filtres,
	identifiantJob,
	onChange,
	simulation,
}: ArbitrageMasseFiltresProps) => {
	const [utilisateurOptions, setUtilisateurOptions] = React.useState<
		DropdownOption[]
	>([]);
	// Listes de filtres sous le trait, repliees par defaut
	const [listesOuvertes, setListesOuvertes] = React.useState(false);

	const { exercice, identifiantEntite, identifiantExercice } = simulation;
	const gestionFonctionnelle = Boolean(exercice?.gestionFonctionnelle);
	const gestionAnalytique = Boolean(exercice?.gestionAnalytique);
	const gestionVentilation = Boolean(exercice?.gestionVentilation);
	const gestionService = Boolean(exercice?.saisieBudgetService);

	const EtiquettesInput = useEtiquette(
		'/api/finances/simulation/etiquettes',
		identifiantEntite,
	);
	// Meme liste de services que dans la comptabilite (selon le role). Le hook
	// recree son composant a chaque rendu, ce qui refermerait la liste a
	// chaque service coche : on le fige une fois les services charges.
	const [ServiceComboboxInstable, , loadingServices] = useService();
	// biome-ignore lint/correctness/useExhaustiveDependencies: le composant ne change qu'a la fin du chargement
	const ServiceCombobox = React.useMemo(
		() => ServiceComboboxInstable,
		[loadingServices],
	);

	// Parametres des listes du referentiel comptable : l'exercice de la
	// simulation, sauf pour les operations, rattachees a l'entite de
	// l'exercice (comme dans CreationImputationModal ; elle peut differer de
	// l'entite de la simulation)
	const paramsExercice = React.useMemo(
		() => ({ identifiantExercice: String(identifiantExercice) }),
		[identifiantExercice],
	);
	const identifiantEntiteExercice = exercice?.identifiantEntite;
	const paramsEntite = React.useMemo(
		() => ({ identifiantEntite: String(identifiantEntiteExercice) }),
		[identifiantEntiteExercice],
	);

	// Nombre de listes repliees ayant une selection, rappele dans le titre pour
	// ne pas filtrer a l'insu de l'utilisateur
	const nbListesActives = [
		filtres.sections,
		filtres.sens,
		filtres.codesChapitre,
		filtres.codesArticle,
		filtres.codesOperation,
		filtres.codesFonction,
		filtres.codesAnalytique,
		filtres.codesVentilation,
		filtres.identifiantsLogin,
		filtres.identifiantsService,
		filtres.identifiantsEtiquette,
	].filter((valeurs) => valeurs.length > 0).length;

	// Utilisateurs ayant saisi des demandes dans le resultat du job courant
	React.useEffect(() => {
		if (!identifiantJob) return undefined;

		// Une reponse arrivee apres un nouveau job est ignoree
		let actif = true;

		const fetchUtilisateurs = async () => {
			try {
				const utilisateurs = await getUtilisateursDetailMontants(
					simulation.identifiant,
					identifiantJob,
				);
				if (!actif) return;

				// Ordre de l'API (nom puis prenom, sans casse ni accents, utilisateurs
				// supprimes en fin de liste). Un utilisateur supprime (prenom/nom a
				// null) porte le meme libelle que sur les cartes.
				setUtilisateurOptions(
					utilisateurs.map((utilisateur) => ({
						'data-testid': `utilisateur${utilisateur.identifiantLogin}`,
						key: utilisateur.identifiantLogin,
						text: formaterNomUtilisateur(utilisateur.prenom, utilisateur.nom),
						value: utilisateur.identifiantLogin,
					})),
				);
			} catch (e) {
				if (actif) {
					toast.error(
						getErrorMessage(e, 'Erreur lors du chargement des utilisateurs'),
					);
				}
			}
		};

		fetchUtilisateurs();

		return () => {
			actif = false;
		};
	}, [identifiantJob, simulation.identifiant]);

	return (
		<Form className="arbitrageMasseFiltres">
			<Form.Group className="arbitrageMasseFiltresLigne">
				<Form.Field width={3}>
					{/* biome-ignore lint/a11y/noLabelWithoutControl: meme presentation que les autres labels du formulaire */}
					<label>Demandé min.</label>
					<MontantFiltreInput
						data-testid="arbitrageMasseFiltreMontantMin"
						onChange={(montantMin) => onChange({ montantMin })}
						value={filtres.montantMin}
					/>
				</Form.Field>
				<Form.Field width={3}>
					{/* biome-ignore lint/a11y/noLabelWithoutControl: meme presentation que les autres labels du formulaire */}
					<label>Demandé max.</label>
					<MontantFiltreInput
						data-testid="arbitrageMasseFiltreMontantMax"
						onChange={(montantMax) => onChange({ montantMax })}
						value={filtres.montantMax}
					/>
				</Form.Field>
				<Form.Field width={5}>
					{/* biome-ignore lint/a11y/noLabelWithoutControl: meme presentation que les autres labels du formulaire */}
					<label>Recherche</label>
					{/* Semantic UI ne gere qu'une icone par champ : la croix pour
					    vider la recherche est posee par-dessus, a droite */}
					<div className="arbitrageMasseRecherche">
						<Input
							data-testid="arbitrageMasseRecherche"
							fluid
							icon="search"
							iconPosition="left"
							input={{ 'data-testid': 'arbitrageMasseRechercheInput' }}
							onChange={(_e, { value }: { value: string }) =>
								onChange({ recherche: value })
							}
							placeholder="Utilisateur, service, commentaire, origine, statut..."
							value={filtres.recherche}
						/>
						{filtres.recherche && (
							<button
								aria-label="Effacer la recherche"
								className="arbitrageMasseRechercheEffacer"
								data-testid="arbitrageMasseRechercheEffacer"
								onClick={() => onChange({ recherche: '' })}
								type="button"
							>
								<Icon iconSet="Lucide" name="X" size={16} />
							</button>
						)}
					</div>
				</Form.Field>
				{/* Meme structure que les champs voisins (libelle, puis zone de la
				    hauteur d'un champ de saisie) pour etre aligne sur eux */}
				<Form.Field width={5}>
					{/* biome-ignore lint/a11y/noLabelWithoutControl: libelle vide, pour l'alignement */}
					<label>&nbsp;</label>
					<div className="arbitrageMasseFiltresToggle">
						<Checkbox
							checked={filtres.inclureTraitees}
							data-testid="arbitrageMasseFiltreInclureTraitees"
							label="Inclure acceptées / refusées"
							onChange={(_e, { checked }) =>
								onChange({ inclureTraitees: Boolean(checked) })
							}
							toggle
						/>
						{/* Titre de l'accordeon des listes (sous le trait) */}
						<Accordion className="arbitrageMasseFiltresAccordeon">
							<Accordion.Title
								active={listesOuvertes}
								data-testid="arbitrageMasseFiltresAccordeonTitre"
								onClick={() => setListesOuvertes((ouvertes) => !ouvertes)}
							>
								<Icon
									color="#004a99"
									iconSet="Lucide"
									name={listesOuvertes ? 'ChevronUp' : 'ChevronDown'}
									strokeWidth={3}
								/>
								<span className="arbitrageMasseFiltresAccordeonLibelle">
									Filtres détaillés
									{nbListesActives > 0 &&
										` (${nbListesActives} actif${nbListesActives > 1 ? 's' : ''})`}
								</span>
							</Accordion.Title>
						</Accordion>
					</div>
				</Form.Field>
			</Form.Group>
			<Accordion>
				<Accordion.Content active={listesOuvertes}>
					<Divider />
					<Form.Group className="arbitrageMasseFiltresGrille">
						<FiltreMultiple
							data-testid="arbitrageMasseFiltreSections"
							label="Sections"
							onChange={(sections: string[]) => onChange({ sections })}
							options={SECTION_OPTIONS}
							placeholder="Toutes les sections"
							value={filtres.sections}
						/>
						<FiltreMultiple
							data-testid="arbitrageMasseFiltreSens"
							label="Sens"
							onChange={(sens: string[]) => onChange({ sens })}
							options={SENS_OPTIONS}
							placeholder="Tous les sens"
							value={filtres.sens}
						/>
						<ArbitrageMasseFiltreReferentiel
							data-testid="arbitrageMasseFiltreChapitres"
							element="chapitres"
							label="Chapitres"
							onChange={(codesChapitre) => onChange({ codesChapitre })}
							params={paramsExercice}
							placeholder="Tous les chapitres"
							value={filtres.codesChapitre}
						/>
						<ArbitrageMasseFiltreReferentiel
							data-testid="arbitrageMasseFiltreArticles"
							element="articles"
							label="Articles"
							onChange={(codesArticle) => onChange({ codesArticle })}
							params={paramsExercice}
							placeholder="Tous les articles"
							value={filtres.codesArticle}
						/>
						<ArbitrageMasseFiltreReferentiel
							data-testid="arbitrageMasseFiltreOperations"
							element="operations"
							label="Opérations"
							onChange={(codesOperation) => onChange({ codesOperation })}
							params={paramsEntite}
							placeholder="Toutes les opérations"
							value={filtres.codesOperation}
						/>
					</Form.Group>
					{/* Suite des elements de l'imputation, selon les niveaux geres */}
					{(gestionFonctionnelle ||
						gestionAnalytique ||
						gestionVentilation) && (
						<Form.Group className="arbitrageMasseFiltresGrille">
							{gestionFonctionnelle && (
								<ArbitrageMasseFiltreReferentiel
									data-testid="arbitrageMasseFiltreFonctions"
									element="fonctions"
									label="Fonctions"
									onChange={(codesFonction) => onChange({ codesFonction })}
									params={paramsExercice}
									placeholder="Toutes les fonctions"
									value={filtres.codesFonction}
								/>
							)}
							{gestionAnalytique && (
								<ArbitrageMasseFiltreReferentiel
									data-testid="arbitrageMasseFiltreAnalytiques"
									element="analytiques"
									label="Analytiques"
									onChange={(codesAnalytique) => onChange({ codesAnalytique })}
									params={paramsExercice}
									placeholder="Toutes les analytiques"
									value={filtres.codesAnalytique}
								/>
							)}
							{gestionVentilation && (
								<ArbitrageMasseFiltreReferentiel
									data-testid="arbitrageMasseFiltreVentilations"
									element="ventilations"
									label="Ventilations"
									onChange={(codesVentilation) =>
										onChange({ codesVentilation })
									}
									params={paramsExercice}
									placeholder="Toutes les ventilations"
									value={filtres.codesVentilation}
								/>
							)}
						</Form.Group>
					)}
					{/* Filtres hors imputation : auteur, service et etiquettes */}
					<Form.Group className="arbitrageMasseFiltresGrille">
						<FiltreMultiple
							data-testid="arbitrageMasseFiltreUtilisateurs"
							label="Utilisateurs"
							onChange={(identifiantsLogin: number[]) =>
								onChange({ identifiantsLogin })
							}
							options={utilisateurOptions}
							placeholder="Tous les utilisateurs"
							value={filtres.identifiantsLogin}
						/>
						{gestionService && (
							<Form.Field data-testid="arbitrageMasseFiltreServices">
								{/* biome-ignore lint/a11y/noLabelWithoutControl: meme presentation que les autres labels du formulaire */}
								<label>Services</label>
								<ServiceCombobox
									// Le hook est prevu pour un seul service : on l'utilise ici
									// en selection multiple
									identifiantService={filtres.identifiantsService as never}
									menuPortalTarget={document.body}
									multiple
									onSelect={(identifiantsService) =>
										onChange({
											identifiantsService:
												(identifiantsService as unknown as number[]) ?? [],
										})
									}
									placeholder="Tous les services"
								/>
							</Form.Field>
						)}
						<Form.Field data-testid="arbitrageMasseFiltreEtiquettes">
							{/* biome-ignore lint/a11y/noLabelWithoutControl: meme presentation que les autres labels du formulaire */}
							<label>Étiquettes</label>
							{/* Meme composant (et memes couleurs) que les etiquettes des
							    cartes, sans possibilite d'en creer depuis un filtre */}
							<EtiquettesInput
								allowAdditions={false}
								colors={COULEURS_ETIQUETTES}
								dropdownProps={{ multiple: true }}
								onChange={({
									etiquettes,
								}: {
									etiquettes: Etiquette[] | Etiquette['identifiant'];
								}) =>
									onChange({
										identifiantsEtiquette: (Array.isArray(etiquettes)
											? etiquettes
											: [{ identifiant: etiquettes } as Etiquette]
										).map((etiquette) => etiquette.identifiant),
									})
								}
								placeholder="Toutes les étiquettes"
								selectedEtiquettes={filtres.identifiantsEtiquette.map(
									(identifiant) => ({ identifiant }) as Etiquette,
								)}
							/>
						</Form.Field>
					</Form.Group>
				</Accordion.Content>
			</Accordion>
		</Form>
	);
};

export default ArbitrageMasseFiltres;
